import React, { useState, useEffect } from 'react';
import { FiMonitor, FiActivity, FiUsers, FiClock, FiMapPin, FiX, FiChevronRight, FiPlay, FiCheckCircle, FiCalendar } from 'react-icons/fi';
import api from '../../utils/api';

const font = "'Outfit', sans-serif";
const C = { text: '#1e293b', sub: '#64748b', border: '#e2e8f0', bg: '#f8fafc', primary: '#10b981', orange: '#f59e0b', red: '#ef4444' };

const AdminMonitor = () => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSession, setSelectedSession] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [roomsLoading, setRoomsLoading] = useState(false);
    const [filter, setFilter] = useState('live');

    useEffect(() => {
        fetchSessions();
        const interval = setInterval(fetchSessions, 15000); // auto-refresh every 15s
        return () => clearInterval(interval);
    }, []);

    const fetchSessions = async () => {
        try {
            const r = await api.get('/api/admin/live-sessions');
            setSessions(r.data.sessions || []);
        } catch (err) {
            console.error(err);
        } finally { setLoading(false); }
    };

    const openSession = async (session) => {
        setSelectedSession(session);
        setRoomsLoading(true);
        try {
            const r = await api.get(`/api/admin/live-sessions/${session.id}/rooms`);
            setRooms(r.data.rooms || []);
        } catch { setRooms([]); }
        finally { setRoomsLoading(false); }
    };

    const liveSessions = sessions.filter(s => s.status === 'live');
    const scheduledSessions = sessions.filter(s => s.status === 'scheduled');
    const endedSessions = sessions.filter(s => s.status === 'ended');

    const shown = filter === 'live' ? liveSessions : filter === 'scheduled' ? scheduledSessions : endedSessions;

    const statusBadge = (status) => {
        const map = {
            live: { bg: '#fef2f2', color: '#dc2626', label: '● LIVE' },
            scheduled: { bg: '#e0e7ff', color: '#4f46e5', label: 'SCHEDULED' },
            ended: { bg: '#f1f5f9', color: '#64748b', label: 'ENDED' },
        };
        const s = map[status] || map.scheduled;
        return <span style={{ padding: '3px 10px', borderRadius: 20, background: s.bg, color: s.color, fontSize: 11, fontWeight: 800 }}>{s.label}</span>;
    };

    return (
        <div style={{ padding: '0px 10px', fontFamily: font, maxWidth: 1200, margin: '0 auto', animation: 'fadeIn 0.4s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 14 }}>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FiMonitor color={C.primary} /> Live Monitoring
                    </h1>
                    <p style={{ margin: 0, color: C.sub, fontSize: 14 }}>Watch active exams, room-wise invigilator details, and real-time status.</p>
                </div>
                {liveSessions.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 12, background: '#fef2f2', border: '1px solid #fecaca' }}>
                        <span style={{ width: 10, height: 10, background: C.red, borderRadius: '50%', animation: 'pulse 1.5s infinite' }} />
                        <span style={{ fontSize: 13, fontWeight: 800, color: '#dc2626' }}>{liveSessions.length} LIVE NOW</span>
                    </div>
                )}
            </div>

            {/* Filter Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {[
                    { key: 'live', label: `Live (${liveSessions.length})`, icon: <FiActivity size={14} />, c: '#dc2626', bg: '#fef2f2' },
                    { key: 'scheduled', label: `Scheduled (${scheduledSessions.length})`, icon: <FiClock size={14} />, c: '#4f46e5', bg: '#e0e7ff' },
                    { key: 'ended', label: `Ended (${endedSessions.length})`, icon: <FiCheckCircle size={14} />, c: '#64748b', bg: '#f1f5f9' },
                ].map(t => (
                    <button key={t.key} onClick={() => setFilter(t.key)} style={{
                        padding: '8px 16px', borderRadius: 10,
                        border: `1.5px solid ${filter === t.key ? t.c : C.border}`,
                        background: filter === t.key ? t.bg : '#fff',
                        color: filter === t.key ? t.c : C.sub,
                        fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: font,
                        display: 'flex', alignItems: 'center', gap: 6
                    }}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div style={{ padding: 40, textAlign: 'center', color: C.sub }}>Loading sessions...</div>
            ) : shown.length === 0 ? (
                <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.border}`, padding: 40, textAlign: 'center', color: C.sub }}>
                    {filter === 'live' ? 'No exams are currently live.' : filter === 'scheduled' ? 'No scheduled sessions.' : 'No ended sessions.'}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {shown.map(session => (
                        <div key={session.id} onClick={() => openSession(session)} style={{
                            background: '#fff', borderRadius: 16,
                            border: `1px solid ${session.status === 'live' ? '#fecaca' : C.border}`,
                            padding: '16px 24px', boxShadow: session.status === 'live' ? '0 4px 16px rgba(239,68,68,0.08)' : '0 2px 8px rgba(0,0,0,0.03)',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16,
                            transition: 'box-shadow 0.2s, transform 0.15s'
                        }}
                            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.boxShadow = session.status === 'live' ? '0 4px 16px rgba(239,68,68,0.08)' : '0 2px 8px rgba(0,0,0,0.03)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            {/* Live pulse */}
                            {session.status === 'live' && (
                                <div style={{ width: 12, height: 12, background: C.red, borderRadius: '50%', animation: 'pulse 1.5s infinite', flexShrink: 0 }} />
                            )}

                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 4 }}>{session.subject_name || 'Exam Session'}</div>
                                <div style={{ fontSize: 12, color: C.sub, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                    <span>{session.department} • Sem {session.semester}</span>
                                    <span>📅 {session.exam_date?.slice(0, 10)}</span>
                                    <span>🕐 {session.exam_time || '—'}</span>
                                    <span>🏛️ {session.hall_count || 0} halls</span>
                                </div>
                            </div>

                            {statusBadge(session.status)}
                            <FiChevronRight size={18} color={C.sub} />
                        </div>
                    ))}
                </div>
            )}

            {/* Room Detail Modal */}
            {selectedSession && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setSelectedSession(null)}>
                    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 750, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', animation: 'slideUp 0.3s ease', overflow: 'hidden' }}>

                        {/* Header */}
                        <div style={{ background: selectedSession.status === 'live' ? 'linear-gradient(135deg, #dc2626, #ef4444)' : selectedSession.status === 'ended' ? 'linear-gradient(135deg, #64748b, #94a3b8)' : 'linear-gradient(135deg, #4f46e5, #6366f1)', padding: '22px 28px', color: '#fff', position: 'relative' }}>
                            <button onClick={() => setSelectedSession(null)} style={{ position: 'absolute', top: 14, right: 14, background: 'rgba(255,255,255,0.2)', border: 'none', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
                                <FiX size={18} />
                            </button>
                            {selectedSession.status === 'live' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                    <span style={{ width: 10, height: 10, background: '#fff', borderRadius: '50%', animation: 'pulse 1.5s infinite' }} />
                                    <span style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>LIVE NOW</span>
                                </div>
                            )}
                            <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 900 }}>{selectedSession.subject_name || 'Exam Session'}</h2>
                            <p style={{ margin: 0, fontSize: 13, opacity: 0.85 }}>
                                {selectedSession.department} • Sem {selectedSession.semester} • {selectedSession.batch_name} • {selectedSession.exam_date?.slice(0, 10)} • {selectedSession.exam_time || '—'}
                            </p>
                        </div>

                        {/* Body */}
                        <div style={{ padding: 24, overflowY: 'auto' }}>
                            {/* Summary Stats */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
                                <div style={{ textAlign: 'center', padding: '14px', borderRadius: 14, background: '#e0e7ff' }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: '#4f46e5' }}>{rooms.length}</div>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase' }}>Rooms Assigned</div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '14px', borderRadius: 14, background: '#d1fae5' }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: '#059669' }}>{rooms.reduce((a, r) => a + r.occupied_seats, 0)}</div>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase' }}>Students Seated</div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '14px', borderRadius: 14, background: '#fef3c7' }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: '#d97706' }}>{rooms.reduce((a, r) => a + r.total_seats, 0)}</div>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase' }}>Total Seats</div>
                                </div>
                            </div>

                            <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 800, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FiMapPin size={16} color={C.primary} /> Room-wise Details
                            </h3>

                            {roomsLoading ? (
                                <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontSize: 13 }}>Loading rooms...</div>
                            ) : rooms.length === 0 ? (
                                <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontSize: 13, background: C.bg, borderRadius: 14, border: `1px solid ${C.border}` }}>
                                    No halls assigned to this session yet.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {rooms.map((room, idx) => (
                                        <div key={room.assignment_id || idx} style={{ background: C.bg, borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                                            {/* Room Header */}
                                            <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${C.border}` }}>
                                                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14 }}>
                                                    <FiMapPin size={18} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{room.hall_number || `Hall ${idx + 1}`}</div>
                                                    <div style={{ fontSize: 12, color: C.sub }}>{room.building} • Capacity: {room.capacity}</div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#4f46e5' }}>
                                                        <FiUsers size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                                        {room.occupied_seats}/{room.total_seats} students
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Invigilator */}
                                            <div style={{ padding: '10px 18px', background: '#fff', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ width: 28, height: 28, borderRadius: 7, background: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <FiUsers size={13} />
                                                </span>
                                                <div>
                                                    <div style={{ fontSize: 11, color: C.sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>Invigilator</div>
                                                    <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{room.invigilator_username || '—'}</div>
                                                </div>
                                            </div>

                                            {/* Seat Grid */}
                                            {room.seats && room.seats.length > 0 && (
                                                <div style={{ padding: '12px 18px' }}>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: C.sub, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.3 }}>Seat Allocations</div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                        {room.seats.map(seat => {
                                                            const hasStudent = seat.student_username && seat.student_username !== '';
                                                            return (
                                                                <div key={seat.id || seat.seat_number} 
                                                                     title={hasStudent ? `${seat.student_name} (${seat.roll_number})` : 'Empty'} 
                                                                     onClick={() => hasStudent && alert(`${seat.student_name} (${seat.roll_number}) is seated here.`)} 
                                                                     style={{
                                                                    width: 36, height: 36, borderRadius: 8,
                                                                    background: hasStudent ? '#d1fae5' : '#f1f5f9',
                                                                    color: hasStudent ? '#059669' : '#cbd5e1',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    fontSize: 11, fontWeight: 800,
                                                                    border: `1px solid ${hasStudent ? '#bbf7d0' : '#e2e8f0'}`,
                                                                    cursor: hasStudent ? 'pointer' : 'default'
                                                                }}>
                                                                    {seat.seat_number}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    <div style={{ fontSize: 10, color: C.sub, marginTop: 6, display: 'flex', gap: 14 }}>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: '#d1fae5', border: '1px solid #bbf7d0' }} /> Occupied</span>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: '#f1f5f9', border: '1px solid #e2e8f0' }} /> Empty</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, background: C.bg, display: 'flex', justifyContent: 'flex-end', borderRadius: '0 0 20px 20px' }}>
                            <button onClick={() => setSelectedSession(null)} style={{ padding: '10px 24px', borderRadius: 8, background: C.primary, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontFamily: font }}>Close</button>
                        </div>
                    </div>
                </div>
            )}


            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes pulse {
                    0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
                    70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
                    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
                }
            `}</style>
        </div>
    );
};

export default AdminMonitor;
